import { useCallback, useEffect, useMemo, useState } from "react";
import useUrlSearch from "./use-url-search";
import IMultiFilter from "system/types/interfaces/IMultiFilter";
import { MachineFilterSet } from "system/types/wireTypes";
import { filterTypes } from "system/libraries/constants";

const factsToFilter: { [name: string]: number } = {
  config: 1,
  adsite: 2,
  mfr: 3,
  model: 4,
  subject: 5,
  computer: 6,
  osname: 7
};

export const hasAnyFilter = (filterSet: MachineFilterSet): boolean => {
  return !!(
    filterSet.adSite ||
    filterSet.computerName ||
    filterSet.configurationName ||
    filterSet.hardwareMfr ||
    filterSet.hardwareModel ||
    filterSet.subject ||
    filterSet.osname
  );
};

const useMultiFilters = () => {
  const searchParams = useUrlSearch();
  const [currentFilters, setCurrentFilters] = useState<{ [name: string]: string[] }>(() => {
    const result: { [name: string]: string[] } = {};
    for (const name in factsToFilter) {
      result[name] = searchParams.getAll(name);
    }
    return result;
  });

  useEffect(() => {
    let filtersChanged = false;
    for (const name in factsToFilter) {
      const urlValues = searchParams.getAll(name);
      const current = currentFilters[name] ?? [];
      if (urlValues.length !== current.length || urlValues.some((v, i) => v !== current[i])) {
        filtersChanged = true;
        break;
      }
    }

    if (!filtersChanged) {
      return;
    }

    let result: { [name: string]: string[] } = {};
    for (const name in factsToFilter) {
      result[name] = searchParams.getAll(name);
    }

    setCurrentFilters(result);
  }, [searchParams, currentFilters, setCurrentFilters]);

  const typeToFilterName = useCallback(
    (type: number): string | null => {
      for (const name in factsToFilter) {
        if (factsToFilter[name] === type) {
          return name;
        }
      }

      return null;
    },
    []
  );

  const setMultiFilter = useCallback(
    (type: number, values: string[] | null) => {
      for (const name in factsToFilter) {
        if (factsToFilter[name] === type) {
          if (values?.length) {
            searchParams.setAll(name, values);
          } else {
            searchParams.set(name, null);
          }
          return;
        }
      }
    },
    [searchParams]
  );

  const clearFilters = useCallback(() => {
    let result: { [name: string]: string | null } = {};
    for (const name in factsToFilter) {
      result[name] = null;
    }

    searchParams.setMany(result);
  }, [searchParams]);

  const currentMultiFilters = useMemo<IMultiFilter[]>(() => {
    let result: IMultiFilter[] = [];

    for (const name in currentFilters) {
      const values = currentFilters[name];
      result.push({
        filterType: factsToFilter[name],
        filterValue: values.length > 0 ? values.join(", ") : null,
      });
    }

    return result;
  }, [currentFilters]);

  const multiFilterSearchString = useMemo<string>(
    () => {
      const params: string[] = [];
      for (const name in currentFilters) {
        for (const value of currentFilters[name]) {
          params.push(`${name}=${encodeURIComponent(value)}`);
        }
      }
      return params.join("&");
    },
    [currentFilters]
  );

  const machineFilterSet = useMemo<MachineFilterSet>(
    () => {
      const toNullableArray = (type: number): string[] | null => {
        const name = typeToFilterName(type);
        if (!name) return null;
        const values = currentFilters[name];
        return values?.length > 0 ? values : null;
      };

      return {
        adSite: toNullableArray(filterTypes.ADSITE),
        computerName: toNullableArray(filterTypes.COMPUTER),
        configurationName: toNullableArray(filterTypes.CONFIG),
        hardwareMfr: toNullableArray(filterTypes.MFR),
        hardwareModel: toNullableArray(filterTypes.MODEL),
        subject: toNullableArray(filterTypes.SUBJECT),
        osname: toNullableArray(filterTypes.OSNAME),
      };
    },
    [currentFilters, typeToFilterName]
  );

  return {
    currentMultiFilters,
    setMultiFilter,
    factsToFilter,
    clearFilters,
    multiFilterSearchString,
    machineFilterSet
  };
};

export default useMultiFilters;
